import { useState, useCallback, useEffect } from "react";
import { BASE_URL } from '../../apiConfig';
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { ShieldCheck, UserRoundCog, CheckCircle, Truck } from 'lucide-react';
import { MdOutlineDeliveryDining as Moto } from "react-icons/md";
import { useAuth } from "../../context/AuthProvider";
import Select from "react-select";

const initialAdminFields = {
  adminName: "",
  adminGender: "",
  adminPhone: "",
};

const initialConfirmatorFields = {
  confirmatorName: "",
  confirmatorGender: "",
  confirmatorPhone: "",
  confirmatorRib: "",
  workStatus: false,
  priceConfirm: "",
  priceWork: "",
  selectedPriceOption: null,
  selectedSellers: [],
};

const initialLivreurFields = {
  livreurName: "",
  livreurPhone: "",
  livreurPrice: "",
  livreurRib: "",
};

const initialFormState = {
  email: "",
  password: "",
  confirmPassword: "",
  role: 1,
  adminName: "",
  adminGender: "",
  adminPhone: "",
  confirmatorName: "",
  confirmatorGender: "",
  confirmatorPhone: "",
  confirmatorRib: "",
  confirmatorPrice: "",
  workStatus: false,
  priceWork: "",
  selectedRoleId: null,
  selectedPriceOption: null,
  selectedSellers: [],
};

export default function CreateUserForm({ roles, activeSellers }) {
  const [formData, setFormData] = useState(initialFormState);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(false);
  const [serverError, setServerError] = useState("");
  const [sellerOptions, setSellerOptions] = useState([]);
  const { token } = useAuth();


  useEffect(() => {
    if (Array.isArray(activeSellers)) {
      const options = activeSellers.map(seller => ({
        value: seller.id,
        label: `${seller.name} (${seller.name_store || seller.store_name || 'No store'})`
      }));
      setSellerOptions(options);
    } else if (activeSellers && typeof activeSellers === 'object') {
      const sellersArray = activeSellers.data || [];
      const options = sellersArray.map(seller => ({
        value: seller.id,
        label: `${seller.name} (${seller.name_store || seller.store_name || 'No store'})`
      }));
      setSellerOptions(options);
    } else {
      setSellerOptions([]);
    }
  }, [activeSellers]);

  


  const handleInputChange = useCallback(
    (e) => {
      const { name, value, type, checked } = e.target;

      setFormData((prevFormData) => {
        let newValue = value;
        const updatedFormData = { ...prevFormData };

        if (name === "role") {
          newValue = Number(value);
          updatedFormData[name] = newValue;

          if (newValue !== prevFormData.role) {
            Object.keys(initialConfirmatorFields).forEach((field) => {
              updatedFormData[field] = initialConfirmatorFields[field];
            });
            Object.keys(initialAdminFields).forEach((field) => {
              updatedFormData[field] = initialAdminFields[field];
            });
            Object.keys(initialLivreurFields).forEach((field) => {
              updatedFormData[field] = initialLivreurFields[field];
            });
            if (newValue !== 2) {
              updatedFormData.selectedRoleId = null;
            }
          }
        } else if (name === "priceOption") {
          updatedFormData.selectedPriceOption = value;
          if (value === "confirm") {
            updatedFormData.priceWork = "";
            updatedFormData.workStatus = false;
          } else if (value === "work") {
            updatedFormData.confirmatorPrice = "";
            updatedFormData.workStatus = true;
          }
        } else if (type === "number") {
          newValue = value === "" ? "" : Number(value);
          updatedFormData[name] = newValue;

          if (updatedFormData.role === 3) {
            if (name === "confirmatorPrice" && newValue !== "") {
              updatedFormData.selectedPriceOption = "confirm";
              updatedFormData.workStatus = false;
            } else if (name === "priceWork" && newValue !== "") {
              updatedFormData.selectedPriceOption = "work";
              updatedFormData.workStatus = true;
            }
          }
        } else if (type === "checkbox") {
          if (name === "workStatus" && updatedFormData.selectedPriceOption === null) {
            updatedFormData[name] = checked;
          }
        } else if (name === "selectedRoleId") {
          updatedFormData.selectedRoleId = Number(value);
        } else {
          updatedFormData[name] = newValue;
        }

        return updatedFormData;
      });

      if (errors[name]) {
        setErrors((prevErrors) => ({ ...prevErrors, [name]: "" }));
      }
      setServerError("");
      setSuccess(false);
    },
    [errors]
  );

  const handleSellerChange = (selectedOptions) => {
    const sellerIds = selectedOptions.map(option => option.value);
    setFormData(prev => ({
      ...prev,
      selectedSellers: sellerIds
    }));
    
    if (errors.selectedSellers) {
      setErrors(prev => ({ ...prev, selectedSellers: "" }));
    }
  };

  const validateEmail = (email) => {
    return /^[^\s.]+.[^\s.]+\.[^\s.]+$/.test(email);
  };
  
  const validateForm = useCallback(() => {
    const newErrors = {};

    if (!formData.email.trim()) {
      newErrors.email = "L'adresse e-mail est obligatoire.";
    } else if (!validateEmail(formData.email)) {
      newErrors.email = "L'adresse e-mail doit être valide.";
    }

    if (!formData.password) {
      newErrors.password = "Le mot de passe est obligatoire.";
    } else if (formData.password.length < 6) {
      newErrors.password = "Le mot de passe doit comporter au moins 6 caractères.";
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Les mots de passe ne correspondent pas.";
    }

    if (!formData.role) {
      newErrors.role = "Le rôle est obligatoire.";
    } else if (![1, 2, 3, 4].includes(formData.role)) {
      newErrors.role = "Le rôle doit être 1 (Admin), 2 (Assistant), 3 (Confirmator) ou 4 (Livreur).";
    }

    if (formData.role === 1 || formData.role === 2) {
      if (!formData.adminName.trim()) {
        newErrors.adminName = "Le nom est obligatoire.";
      }
      if (!formData.adminGender) {
        newErrors.adminGender = "Le sexe est obligatoire.";
      }
      if (!formData.adminPhone.trim()) {
        newErrors.adminPhone = "Le numéro de téléphone est obligatoire.";
      } else if (!/^\d+$/.test(formData.adminPhone.trim())) {
        newErrors.adminPhone = "Le numéro de téléphone doit être un nombre.";
      }

      if (formData.role === 2) {
        if (!roles || roles.length === 0) {
          newErrors.selectedRoleId = "Aucun rôle spécifique d'assistant n'est disponible. Veuillez contacter l'administrateur.";
        } else if (formData.selectedRoleId === null) {
          newErrors.selectedRoleId = "Veuillez sélectionner un rôle spécifique pour l'assistant.";
        }
      }
    } else if (formData.role === 3) {
      if (!formData.confirmatorName.trim()) {
        newErrors.confirmatorName = "Le nom est obligatoire.";
      }
      if (!formData.confirmatorGender) {
        newErrors.confirmatorGender = "Le sexe est obligatoire.";
      }
      if (!formData.confirmatorPhone.trim()) {
        newErrors.confirmatorPhone = "Le numéro de téléphone est obligatoire.";
      } else if (!/^\d+$/.test(formData.confirmatorPhone.trim())) {
        newErrors.confirmatorPhone = "Le numéro de téléphone doit être un nombre.";
      }
      if (!formData.confirmatorRib.trim()) {
        newErrors.confirmatorRib = "Le RIB est obligatoire pour les rôles 3.";
      } else if (!/^\d+$/.test(formData.confirmatorRib.trim())) {
        newErrors.confirmatorRib = "Le RIB doit être un nombre.";
      }

      if (!formData.selectedPriceOption) {
        newErrors.confirmatorPrice = "Veuillez sélectionner un type de prix (par confirmation ou par travail).";
        newErrors.priceWork = "Veuillez sélectionner un type de prix (par confirmation ou par travail).";
      } else if (formData.selectedPriceOption === "confirm") {
        if (formData.confirmatorPrice === "" || formData.confirmatorPrice === null) {
          newErrors.confirmatorPrice = "Le tarif de confirmation est obligatoire.";
        } else if (typeof formData.confirmatorPrice !== 'number' || formData.confirmatorPrice < 0) {
          newErrors.confirmatorPrice = "Le tarif de confirmation doit être un nombre positif.";
        }
      } else if (formData.selectedPriceOption === "work") {
        if (formData.priceWork === "" || formData.priceWork === null) {
          newErrors.priceWork = "Le tarif de travail est obligatoire.";
        } else if (typeof formData.priceWork !== 'number' || formData.priceWork < 0) {
          newErrors.priceWork = "Le tarif de travail doit être un nombre positif.";
        }
        }
      } else if (formData.role === 4) {
        if (!formData.livreurName.trim()) {
           newErrors.livreurName = "Le nom est obligatoire.";
        }
        if (!formData.livreurPhone.trim()) {
           newErrors.livreurPhone = "Le numéro de téléphone est obligatoire.";
        } else if (!/^\d+$/.test(formData.livreurPhone.trim())) {
           newErrors.livreurPhone = "Le numéro de téléphone doit être un nombre.";
        }
        if (!formData.livreurPrice) {
           newErrors.livreurPrice = "Le prix de livraison est obligatoire.";
        } else if (isNaN(formData.livreurPrice)) {
           newErrors.livreurPrice = "Le prix doit être un nombre.";
        }
      }
      
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, roles]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    setServerError("");
    setSuccess(false);

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const payload = {
        email: formData.email,
        password: formData.password,
        role: formData.role,
      };

      if (formData.role === 1) {
        payload.name = formData.adminName;
        payload.gender = formData.adminGender;
        payload.phone = formData.adminPhone;
      } else if (formData.role === 2) {
        payload.name = formData.adminName;
        payload.gender = formData.adminGender;
        payload.phone = formData.adminPhone;
        payload.specific_role_id = formData.selectedRoleId;
      } else if (formData.role === 3) {
        payload.name = formData.confirmatorName;
        payload.gender = formData.confirmatorGender;
        payload.phone = formData.confirmatorPhone;
        payload.rib = formData.confirmatorRib;
        payload.work_status = formData.workStatus;
        payload.seller_ids = formData.selectedSellers; // Add seller IDs to payload

        if (formData.selectedPriceOption === "confirm" && formData.confirmatorPrice !== "" && formData.confirmatorPrice !== null) {
          payload.price_confirm = formData.confirmatorPrice;
        } else if (formData.selectedPriceOption === "work" && formData.priceWork !== "" && formData.priceWork !== null) {
          payload.price_work = formData.priceWork;
        }
      } else if (formData.role === 4) {
        payload.name = formData.livreurName;
        payload.phone = formData.livreurPhone;
        payload.prix_delivery = formData.livreurPrice;
        payload.rib = formData.livreurRib;
      }

      console.log("Sending payload:", payload);

      const response = await fetch(`${BASE_URL}/api/register-staff`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 422 && data.errors) {
          const newErrors = {};
          for (const key in data.errors) {
            let clientKey = key;
            if (key === 'name' && (formData.role === 1 || formData.role === 2)) clientKey = 'adminName';
            if (key === 'gender' && (formData.role === 1 || formData.role === 2)) clientKey = 'adminGender';
            if (key === 'phone' && (formData.role === 1 || formData.role === 2)) clientKey = 'adminPhone';
            if (key === 'specific_role_id' && formData.role === 2) clientKey = 'selectedRoleId';
            if (key === 'name' && formData.role === 3) clientKey = 'confirmatorName';
            if (key === 'gender' && formData.role === 3) clientKey = 'confirmatorGender';
            if (key === 'phone' && formData.role === 3) clientKey = 'confirmatorPhone';
            if (key === 'rib' && formData.role === 3) clientKey = 'confirmatorRib';
            if (key === 'price_confirm' && formData.role === 3) clientKey = 'confirmatorPrice';
            if (key === 'price_work' && formData.role === 3) clientKey = 'priceWork';
            if (key === 'seller_ids' && formData.role === 3) clientKey = 'selectedSellers';
            if (key === 'name' && formData.role === 4) clientKey = 'livreurName';
            if (key === 'phone' && formData.role === 4) clientKey = 'livreurPhone';
            if (key === 'prix_delivery' && formData.role === 4) clientKey = 'livreurPrice';
            if (key === 'rib' && formData.role === 4) clientKey = 'livreurRib';

            newErrors[clientKey] = data.errors[key][0];
          }
          setErrors(newErrors);
          setServerError("Veuillez corriger les erreurs dans le formulaire.");
        } else {
          if(response.status === 403){
            toast.error(data?.message || "You don't have permissions!");
          }else{
            toast.error("Erreur de création de l'utilisateur!");
          }
          setServerError(data.message || "Une erreur inattendue est survenue.");
        }
        console.error("Server Error:", data);
        return;
      }

      setSuccess(true);
      setErrors({});
      setServerError("");
      toast.success("Utilisateur créé avec succès !");
      setTimeout(() => {
        setFormData(initialFormState);
        setSuccess(false);
      }, 1000);
    } catch (error) {
      setServerError("Échec de la connexion au serveur. Veuillez réessayer.");
    } finally {
      setIsLoading(false);
    }
  };

  const roleOptions = [
    { value: 1, label: "Admin", icon: ShieldCheck  },
    { value: 2, label: "Assistant", icon: UserRoundCog  },
    { value: 3, label: "Confirmator", icon: CheckCircle },
    { value: 4, label: "Livreur", icon: Moto },
  ];

  const selectedSellerOptions = sellerOptions.filter(option => 
    formData.selectedSellers.includes(option.value)
  );

  return (
    <div className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden font-sans">
      {success && (
        <div className="bg-[var(--color-primary-500)] text-white p-6 text-center animate-fadeIn rounded-t-3xl">
          <div className="flex items-center justify-center mb-2">
            <svg className="h-8 w-8 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span className="text-lg font-semibold">Utilisateur créé avec succès !</span>
          </div>
          <p className="text-[var(--color-primary-50)] text-xs">Le nouvel utilisateur a été ajouté au système.</p>
        </div>
      )}

      {serverError && (
        <div className="bg-red-500 text-white p-6 text-center animate-fadeIn rounded-t-3xl">
          <div className="flex items-center justify-center mb-2">
            <svg className="h-8 w-8 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-xl font-semibold">Erreur !</span>
          </div>
          {/* <p>{serverError}</p> */}
        </div>
      )}

      <form onSubmit={handleSubmit} className="p-8">
        <div className="mb-12">
          <h2 className="text-lg font-bold text-gray-900 mb-6">Informations de base</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <label htmlFor="email" className="block text-sm font-semibold text-gray-900 mb-3">
                Adresse e-mail *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="utilisateur.example.com"
                className={`w-full py-3 px-4 outline-none text-xs border-2 rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white ${
                  errors.email
                    ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100"
                    : "border-gray-200 focus:border-[var(--color-primary-200)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
                }`}
                aria-invalid={errors.email ? "true" : "false"}
                aria-describedby="email-error"
              />
              {errors.email && <p id="email-error" className="mt-2 text-red-600 text-xs">{errors.email}</p>}
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-semibold text-gray-900 mb-3">
                Mot de passe *
              </label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                placeholder="Entrez le mot de passe..."
                className={`w-full py-3 px-4 outline-none text-xs border-2 rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white ${
                  errors.password
                    ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100"
                    : "border-gray-200 focus:border-[var(--color-primary-200)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
                }`}
                aria-invalid={errors.password ? "true" : "false"}
                aria-describedby="password-error"
              />
              {errors.password && <p id="password-error" className="mt-2 text-red-600 text-xs">{errors.password}</p>}
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-semibold text-gray-900 mb-3">
                Confirmez le mot de passe *
              </label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                placeholder="Confirmez le mot de passe..."
                className={`w-full py-3 px-4 outline-none text-xs border-2 rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white ${
                  errors.confirmPassword
                    ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100"
                    : "border-gray-200 focus:border-[var(--color-primary-200)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
                }`}
                aria-invalid={errors.confirmPassword ? "true" : "false"}
                aria-describedby="confirmPassword-error"
              />
              {errors.confirmPassword && <p id="confirmPassword-error" className="mt-2 text-red-600 text-xs">{errors.confirmPassword}</p>}
            </div>
          </div>
        </div>

        <div className="mb-12">
          <h2 className="text-lg font-bold text-gray-900 mb-6">Rôle de l'utilisateur</h2>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
            {roleOptions.map((option) => (
              <label
                key={option.value}
                className={`p-6 rounded-2xl cursor-pointer transition-all duration-200 border-2 ${
                  formData.role === option.value
                    ? "bg-[var(--color-primary-50)] border-[var(--color-primary-500)] text-[var(--color-primary-500)]"
                    : "bg-gray-50 border-gray-200 hover:border-[var(--color-primary-500)] text-gray-700"
                }`}
              >
                <input
                  type="radio"
                  name="role"
                  value={option.value}
                  checked={formData.role === option.value}
                  onChange={handleInputChange}
                  className="sr-only"
                  aria-labelledby={`role-label-${option.value}`}
                  aria-describedby={`role-icon-${option.value}`}
                />
                <div className="text-center flex-1 space-y-1">
                  <div id={`role-icon-${option.value}`} className=" opacity-75 flex justify-center"><option.icon size={35} /> </div>
                  <div id={`role-label-${option.value}`} className="font-semibold mb-2">{option.label}</div>
                </div>
              </label>
            ))}
          </div>
          {errors.role && <p id="role-error" className="mt-2 text-red-600 text-xs text-center">{errors.role}</p>}
        </div>

        {(formData.role === 1 || formData.role === 2) && (
          <div className="mb-12">
            <h2 className="text-lg font-bold text-gray-900 mb-6">Informations de l'administrateur</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <label htmlFor="adminName" className="block text-sm font-semibold text-gray-900 mb-3">
                  Nom complet *
                </label>
                <input
                  type="text"
                  id="adminName"
                  name="adminName"
                  value={formData.adminName}
                  onChange={handleInputChange}
                  placeholder="Entrez le nom complet..."
                  className={`w-full py-3 px-4 text-xs outline-none border-2 rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white ${
                    errors.adminName
                      ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100"
                      : "border-gray-200 focus:border-[var(--color-primary-200)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
                  }`}
                  aria-invalid={errors.adminName ? "true" : "false"}
                  aria-describedby="adminName-error"
                />
                {errors.adminName && <p id="adminName-error" className="mt-2 text-red-600 text-xs">{errors.adminName}</p>}
              </div>

              <div>
                <label htmlFor="adminGender" className="block font-semibold text-sm text-gray-900 mb-3">
                  Sexe *
                </label>
                <select
                  id="adminGender"
                  name="adminGender"
                  value={formData.adminGender}
                  onChange={handleInputChange}
                  className={`w-full py-3 px-4 text-xs outline-none border-2 rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white ${
                    errors.adminGender
                      ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100"
                      : "border-gray-200 focus:border-[var(--color-primary-200)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
                  }`}
                  aria-invalid={errors.adminGender ? "true" : "false"}
                  aria-describedby="adminGender-error"
                >
                  <option value="">Sélectionnez le sexe</option>
                  <option value="Male">Homme</option>
                  <option value="Female">Femme</option>
                </select>
                {errors.adminGender && <p id="adminGender-error" className="mt-2 text-red-600 text-xs">{errors.adminGender}</p>}
              </div>

              <div>
                <label htmlFor="adminPhone" className="block text-sm font-semibold text-gray-900 mb-3">
                  Numéro de téléphone *
                </label>
                <input
                  type="text"
                  id="adminPhone"
                  name="adminPhone"
                  value={formData.adminPhone}
                  onChange={handleInputChange}
                  placeholder="0612345678"
                  className={`w-full py-3 px-4 text-xs outline-none border-2 rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white ${
                    errors.adminPhone
                      ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100"
                      : "border-gray-200 focus:border-[var(--color-primary-200)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
                  }`}
                  aria-invalid={errors.adminPhone ? "true" : "false"}
                  aria-describedby="adminPhone-error"
                />
                {errors.adminPhone && <p id="adminPhone-error" className="mt-2 text-red-600 text-xs">{errors.adminPhone}</p>}
              </div>
            </div>
          </div>
        )}

        {formData.role === 2 && roles && roles.length > 0 && (
          <div className="mb-12">
            <h2 className="text-lg font-bold text-gray-900 mb-6">Rôle Spécifique de l'Assistant</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {roles.map((role) => (
                <label
                  key={role.id}
                  className={`flex items-center p-3 rounded-2xl cursor-pointer transition-all duration-200 border-2 ${
                    formData.selectedRoleId === role.id
                      ? "bg-[var(--color-primary-50)] border-[var(--color-primary-500)] text-[var(--color-primary-500)]"
                      : "bg-gray-50 border-gray-200 hover:border-[var(--color-primary-500)] text-gray-700"
                  }`}
                >
                  <input
                    type="radio"
                    name="selectedRoleId"
                    value={role.id}
                    checked={formData.selectedRoleId === role.id}
                    onChange={handleInputChange}
                    className="form-radio h-4 w-4 text-[var(--color-primary-500)] rounded-full border-gray-300 focus:ring-[var(--color-primary-500)] mr-3"
                  />
                  <span className="font-semibold text-sm">{role.name}</span>
                </label>
              ))}
            </div>
            {errors.selectedRoleId && <p id="selectedRoleId-error" className="mt-2 text-red-600 text-xs">{errors.selectedRoleId}</p>}
          </div>
        )}

        {formData.role === 4 && (
          <div className="mb-12">
            <h2 className="text-lg font-bold text-gray-900 mb-6">Informations du Livreur</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <label htmlFor="livreurName" className="block text-sm font-semibold text-gray-900 mb-3">
                  Nom complet *
                </label>
                <input
                  type="text"
                  id="livreurName"
                  name="livreurName"
                  value={formData.livreurName}
                  onChange={handleInputChange}
                  placeholder="Entrez le nom complet..."
                  className={`w-full py-3 px-4 text-xs outline-none border-2 rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white ${
                    errors.livreurName
                      ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100"
                      : "border-gray-200 focus:border-[var(--color-primary-200)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
                  }`}
                  aria-invalid={errors.livreurName ? "true" : "false"}
                  aria-describedby="livreurName-error"
                />
                {errors.livreurName && <p id="livreurName-error" className="mt-2 text-red-600 text-xs">{errors.livreurName}</p>}
              </div>

               <div>
                <label htmlFor="livreurPhone" className="block text-sm font-semibold text-gray-900 mb-3">
                  Numéro de téléphone *
                </label>
                <input
                  type="text"
                  id="livreurPhone"
                  name="livreurPhone"
                  value={formData.livreurPhone}
                  onChange={handleInputChange}
                  placeholder="0612345678"
                  className={`w-full py-3 px-4 text-xs outline-none border-2 rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white ${
                    errors.livreurPhone
                      ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100"
                      : "border-gray-200 focus:border-[var(--color-primary-200)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
                  }`}
                  aria-invalid={errors.livreurPhone ? "true" : "false"}
                  aria-describedby="livreurPhone-error"
                />
                {errors.livreurPhone && <p id="livreurPhone-error" className="mt-2 text-red-600 text-xs">{errors.livreurPhone}</p>}
              </div>

              <div>
                <label htmlFor="livreurPrice" className="block text-sm font-semibold text-gray-900 mb-3">
                  Prix de livraison (DH) *
                </label>
                <input
                  type="number"
                  id="livreurPrice"
                  name="livreurPrice"
                  value={formData.livreurPrice}
                  onChange={handleInputChange}
                  placeholder="20.00"
                  step="0.01"
                  min="0"
                  className={`w-full py-3 px-4 text-xs outline-none border-2 rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white ${
                    errors.livreurPrice
                      ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100"
                      : "border-gray-200 focus:border-[var(--color-primary-200)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
                  }`}
                  aria-invalid={errors.livreurPrice ? "true" : "false"}
                  aria-describedby="livreurPrice-error"
                />
                {errors.livreurPrice && <p id="livreurPrice-error" className="mt-2 text-red-600 text-xs">{errors.livreurPrice}</p>}
              </div>

              <div>
                <label htmlFor="livreurRib" className="block text-sm font-semibold text-gray-900 mb-3">
                    RIB (Optionnel)
                </label>
                <input
                  type="text"
                  id="livreurRib"
                  name="livreurRib"
                  value={formData.livreurRib}
                  onChange={handleInputChange}
                  placeholder="Entrez le RIB..."
                  className={`w-full py-3 px-4 text-xs outline-none border-2 rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white ${
                    errors.livreurRib
                      ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100"
                      : "border-gray-200 focus:border-[var(--color-primary-200)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
                  }`}
                   aria-invalid={errors.livreurRib ? "true" : "false"}
                  aria-describedby="livreurRib-error"
                />
                {errors.livreurRib && <p id="livreurRib-error" className="mt-2 text-red-600 text-xs">{errors.livreurRib}</p>}
              </div>
            </div>
          </div>
        )}

        {formData.role === 3 && (
          <div className="mb-12">
            <h2 className="text-lg font-bold text-gray-900 mb-6">Informations du Confirmateur</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <label htmlFor="confirmatorName" className="block text-sm font-semibold text-gray-900 mb-3">
                  Nom complet *
                </label>
                <input
                  type="text"
                  id="confirmatorName"
                  name="confirmatorName"
                  value={formData.confirmatorName}
                  onChange={handleInputChange}
                  placeholder="Entrez le nom complet..."
                  className={`w-full py-3 px-4 text-xs outline-none border-2 rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white ${
                    errors.confirmatorName
                      ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100"
                      : "border-gray-200 focus:border-[var(--color-primary-200)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
                  }`}
                  aria-invalid={errors.confirmatorName ? "true" : "false"}
                  aria-describedby="confirmatorName-error"
                />
                {errors.confirmatorName && (
                  <p id="confirmatorName-error" className="mt-2 text-red-600 text-xs">{errors.confirmatorName}</p>
                )}
              </div>

              <div>
                <label htmlFor="confirmatorGender" className="block text-sm font-semibold text-gray-900 mb-3">
                  Sexe *
                </label>
                <select
                  id="confirmatorGender"
                  name="confirmatorGender"
                  value={formData.confirmatorGender}
                  onChange={handleInputChange}
                  className={`w-full py-3 px-4 text-xs border-2 outline-none rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white ${
                    errors.confirmatorGender
                      ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100"
                      : "border-gray-200 focus:border-[var(--color-primary-200)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
                  }`}
                  aria-invalid={errors.confirmatorGender ? "true" : "false"}
                  aria-describedby="confirmatorGender-error"
                >
                  <option value="">Sélectionnez le sexe</option>
                  <option value="Male">Homme</option>
                  <option value="Female">Femme</option>
                </select>
                {errors.confirmatorGender && <p id="confirmatorGender-error" className="mt-2 text-red-600 text-xs">{errors.confirmatorGender}</p>}
              </div>

              <div>
                <label htmlFor="confirmatorPhone" className="block text-sm font-semibold text-gray-900 mb-3">
                  Numéro de téléphone *
                </label>
                <input
                  type="text"
                  id="confirmatorPhone"
                  name="confirmatorPhone"
                  value={formData.confirmatorPhone}
                  onChange={handleInputChange}
                  placeholder="0612345678"
                  className={`w-full py-3 px-4 text-xs outline-none border-2 rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white ${
                    errors.confirmatorPhone
                      ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100"
                      : "border-gray-200 focus:border-[var(--color-primary-200)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
                  }`}
                  aria-invalid={errors.confirmatorPhone ? "true" : "false"}
                  aria-describedby="confirmatorPhone-error"
                />
                {errors.confirmatorPhone && <p id="confirmatorPhone-error" className="mt-2 text-red-600 text-xs">{errors.confirmatorPhone}</p>}
              </div>

              <div>
                <label htmlFor="confirmatorRib" className="block text-sm font-semibold text-gray-900 mb-3">
                  Numéro RIB *
                </label>
                <input
                  type="text"
                  id="confirmatorRib"
                  name="confirmatorRib"
                  value={formData.confirmatorRib}
                  onChange={handleInputChange}
                  placeholder="12345678901234567890"
                  className={`w-full py-3 px-4 text-xs outline-none border-2 rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white ${
                    errors.confirmatorRib
                      ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100"
                      : "border-gray-200 focus:border-[var(--color-primary-200)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
                  }`}
                  aria-invalid={errors.confirmatorRib ? "true" : "false"}
                  aria-describedby="confirmatorRib-error"
                />
                {errors.confirmatorRib && <p id="confirmatorRib-error" className="mt-2 text-red-600 text-xs">{errors.confirmatorRib}</p>}
              </div>

            

      <div className="md:col-span-2">
        <div className="flex items-center mb-4">
          <h3 className="text-lg font-bold text-gray-800">Vendeurs assignés</h3>
        </div>
        <p className="text-gray-600 mb-4 text-xs">
          Sélectionnez les vendeurs avec lesquels ce confirmateur peut travailler:
        </p>
        
        <Select
          isMulti
          options={sellerOptions}
          value={selectedSellerOptions}
          onChange={handleSellerChange}
          className={`w-full py-0.5 px-1 text-xs outline-none border-2 rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white ${
            errors.selectedSellers
              ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100"
              : "border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
          }`}
          styles={{
            control: (base) => ({
              ...base,
              backgroundColor: "transparent",
              boxShadow: "none",
              border: "none",
              cursor: "pointer",
            }),
          }}
          placeholder="Rechercher et sélectionner des vendeurs..."
          noOptionsMessage={() => "Aucun vendeur trouvé"}
          isSearchable
        />
        {errors.selectedSellers && <p className="mt-2 text-red-600 text-xs">{errors.selectedSellers}</p>}
        
        {formData.selectedSellers.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2">
            {selectedSellerOptions.map(option => (
              <div
                key={option.value}
                className="flex items-center text-[var(--color-primary-500)] bg-[var(--color-primary-50)] border border-[var(--color-primary-500)] rounded-full px-3 pb-1 pt-[3px] text-[11px]"
              >
                <span>{option.label}</span>
                <button
                  type="button"
                  onClick={() => handleSellerChange(selectedSellerOptions.filter(opt => opt.value !== option.value))}
                  className="ml-2 text-lg text-[var(--color-primary-500)] hover:text-[var(--color-primary-50)]0 cursor-pointer"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

              <div className="my-5 md:col-span-2">
                <label htmlFor="priceOptionWork" className="block mb-2 w-40 text-sm font-semibold text-gray-900">
                  Prix *
                </label>
                <div className="flex mb-5">
                  <div className="flex items-center gap-2">
                    <input
                      type="radio"
                      id="priceOptionConfirm"
                      name="priceOption"
                      value="confirm"
                      checked={formData.selectedPriceOption === "confirm"}
                      onChange={handleInputChange}
                      className="appearance-none cursor-pointer w-9 focus:outline-none checked:bg-[var(--color-primary-200)]  h-5 bg-gray-300 rounded-full before:inline-block before:rounded-full checked:before:bg-[var(--color-primary-500)] before:bg-gray-500 before:h-4 before:w-4 checked:before:translate-x-full shadow-inner transition-all duration-300 before:mt-0.5 before:ml-0.5"
                    />
                    <label htmlFor="priceOptionConfirm" className="block w-40 text-sm font-semibold text-gray-900">
                      Prix par confirmation *
                    </label>
                  </div>
                  <div className="w-70">
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">MAD</span>
                      <input
                        type="number"
                        id="confirmatorPrice"
                        name="confirmatorPrice"
                        value={formData.confirmatorPrice}
                        onChange={handleInputChange}
                        placeholder="25.00"
                        step="0.01"
                        min="0"
                        disabled={formData.selectedPriceOption !== "confirm"}
                        className={`w-full pl-[60px] py-3 px-4 text-xs outline-none border-2 rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white ${
                          formData.selectedPriceOption === "confirm" ?
                            `border-[var(--color-primary-200)] ring-4 ring-[var(--color-primary-100)]`
                            :
                          errors.confirmatorPrice
                            ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100"
                            : "border-gray-200 focus:border-[var(--color-primary-200)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
                        }`}
                        aria-invalid={errors.confirmatorPrice ? "true" : "false"}
                        aria-describedby="confirmatorPrice-error"
                      />
                    </div>
                    {errors.confirmatorPrice && <p id="confirmatorPrice-error" className="mt-2 text-red-600 text-xs">{errors.confirmatorPrice}</p>}
                  </div>
                </div>
                <div className="flex">
                  <div className="flex items-center gap-2">
                    <input
                      type="radio"
                      id="priceOptionWork"
                      name="priceOption"
                      value="work"
                      checked={formData.selectedPriceOption === "work"}
                      onChange={handleInputChange}
                      className="appearance-none cursor-pointer w-9 focus:outline-none checked:bg-[var(--color-primary-200)]  h-5 bg-gray-300 rounded-full before:inline-block before:rounded-full checked:before:bg-[var(--color-primary-500)] before:bg-gray-500 before:h-4 before:w-4 checked:before:translate-x-full shadow-inner transition-all duration-300 before:mt-0.5 before:ml-0.5"
                    />
                    <label htmlFor="priceOptionWork" className="block w-40 text-sm font-semibold text-gray-900">
                      Prix par travail *
                    </label>
                  </div>
                  <div className="w-70">
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">MAD</span>
                      <input
                        type="number"
                        id="priceWork"
                        name="priceWork"
                        value={formData.priceWork}
                        onChange={handleInputChange}
                        placeholder="500.00"
                        step="0.01"
                        min="0"
                        disabled={formData.selectedPriceOption !== "work"}
                        className={`w-full pl-[60px] py-3 px-4 text-xs outline-none border-2 rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white 
                        ${
                          formData.selectedPriceOption === "work" ?
                          `border-[var(--color-primary-200)] ring-4 ring-[var(--color-primary-100)]`
                          :
                          errors.priceWork
                            ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100"
                            : "border-gray-200 focus:border-[var(--color-primary-200)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
                        }`}
                        aria-invalid={errors.priceWork ? "true" : "false"}
                        aria-describedby="priceWork-error"
                      />
                    </div>
                    {errors.priceWork && <p id="priceWork-error" className="mt-2 text-red-600 text-xs">{errors.priceWork}</p>}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="flex gap-2 pt-6 border-t border-gray-200">
          <Link onClick={()=>{ setFormData(initialFormState) }} className="block cursor-pointer text-center flex-1 py-3 px-4 cursor-pointer text-sm border-2 border-gray-200 text-gray-700 font-semibold rounded-2xl hover:border-gray-300 hover:bg-gray-50 transition-all duration-200">
            <button
              type="button"
              disabled={isLoading}
              className='cursor-pointer'
            >
              Annuler  
            </button>
          </Link> 
          <button
            type="submit"
            disabled={isLoading}
            className={`flex-1 cursor-pointer py-3 px-4 text-sm font-semibold rounded-2xl transition-all duration-200 ${
              isLoading
                ? "bg-gray-400 text-white cursor-not-allowed"
                : "bg-[var(--color-primary-500)] hover:bg-[var(--color-primary-400)] text-white shadow-lg hover:shadow-xl transform hover:scale-105"
            }`}
          >
            {isLoading ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Sauvegarde...
              </span>
            ) : (
              <span className="flex items-center justify-center">
                Créer l'utilisateur
                <svg className="h-4 w-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </span>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}